import { put, takeEvery, takeLatest, call, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import {
  setPicTreeRegularSeasonEventPageData,
  setPicTreeRegularSeasonEventPageLoading,
  setPageTopLoader,
} from "actions";
import { getJwtToken, getDataFromSxi } from "api";

const COMPANY_NAME_BY_CODE = {
  NTT_ME: "NTT ME",
  TEPCO: "東京電力 TEPCO",
  NTT_FT: "NTT FT",
  HEPCO: "ほくでん",
};

const ALL_BOOST_EVENTS_VALUE = "__ALL__";
const KPI_SQL_TEXT_COLLATION = "utf8mb4_unicode_ci";

const SXI_HEAVY_RETRY = {
  timeoutMs: 60000,
  retries: 1,
  backoffBase: 1500,
};

function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,
    retries = 2,
    backoffBase = 1000,
    backoffFactor = 2,
    jitter = true,
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) {
        throw new Error(`timeout after ${timeoutMs}ms`);
      }
      return response;
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err;
      }

      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
    } finally {
      if (yield cancelled()) {
        // no-op
      }
    }
  }
}

const normalizeSelectionSeasonIds = (selectedSeasonIds, selectedSeasonId) => {
  if (Array.isArray(selectedSeasonIds) && selectedSeasonIds.length > 0) {
    return selectedSeasonIds.filter(Boolean);
  }

  return selectedSeasonId ? [selectedSeasonId] : [];
};

const sortSeasonIds = (seasonIds) => Array.from(seasonIds || [])
  .slice()
  .filter(Boolean)
  .filter((seasonId, index, ids) => ids.indexOf(seasonId) === index);

const resolveSeasonLabel = (seasonId, seasonName) => {
  const explicitName = String(seasonName || "").trim();
  if (explicitName) {
    return explicitName;
  }

  return String(seasonId || "");
};

const formatNumber = (value) => Number(value || 0).toLocaleString("ja-JP");

const formatCurrency = (value) => `¥${formatNumber(Number(value || 0))}`;

const emptyTrend = () => ({
  date: [],
  uniqueShots: [],
  reviewerOk: [],
  uniqueShooters: [],
});

const emptySeasonChart = () => ({
  labels: [],
  series: [],
});

const emptyKpis = () => ([
  {
    key: "uniqueShot",
    title: "ユニーク撮影本数",
    value: "--",
    loading: true,
  },
  {
    key: "achievementRate",
    title: "撮影達成率",
    value: "--",
    subtitle: "対象 --本",
    loading: true,
  },
  {
    key: "reviewOk",
    title: "レビューOK数",
    value: "--",
    subtitle: "OK率 --%",
    loading: true,
  },
  {
    key: "uniqueShooter",
    title: "ユニーク撮影者数",
    value: "--",
    subtitle: "累計参加者",
    loading: true,
  },
  {
    key: "firstShooter",
    title: "初回撮影者数",
    value: "--",
    subtitle: "今期新規",
    loading: true,
  },
]);

const readyEmptyKpis = () => emptyKpis().map((item) => ({
  ...item,
  loading: false,
}));

const emptyPageData = (selectedSeasonId, selectedSeasonIds = selectedSeasonId ? [selectedSeasonId] : [], selectedEventKeys = []) => ({
  selectedSeasonId,
  selectedSeasonIds,
  selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
  selectedEventKeys,
  draftSelectedEventKeys: selectedEventKeys,
  selectedDateRange: [null, null],
  draftDateRange: [null, null],
  error: null,
  kpis: emptyKpis(),
  trend: emptyTrend(),
  trendLoading: true,
  trendRows: [],
  trendRowsLoading: false,
  seasonChartLoading: false,
  seasonChart: emptySeasonChart(),
  bootstrapEventLoading: false,
  bootstrapEventReady: false,
  eventRowsLoading: true,
  eventRows: [],
  eventRowsError: null,
});

const isAllBoostEvents = (boostEventId) => !boostEventId || boostEventId === ALL_BOOST_EVENTS_VALUE;

const escapeSqlLiteral = (value) => String(value || "").replace(/'/g, "''");
const collatedSqlNull = () => `CAST(NULL AS CHAR CHARACTER SET utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION}`;

const normalizeSelectedEventKeys = (selectedEventKeys, eventRows = [], selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const sourceKeys = Array.isArray(selectedEventKeys) && selectedEventKeys.length > 0
    ? selectedEventKeys
    : (!isAllBoostEvents(selectedBoostEventId)
      ? eventRows
        .filter((row) => row.id === selectedBoostEventId)
        .slice(0, 1)
        .map((row) => ({ gameSpaceId: row.seasonId, boostEventId: row.id }))
      : []);

  return sourceKeys
    .map((key) => ({
      gameSpaceId: key?.gameSpaceId || key?.game_space_id,
      boostEventId: key?.boostEventId || key?.boost_event_id,
    }))
    .filter((key) => key.gameSpaceId && key.boostEventId)
    .filter((key, index, keys) => keys.findIndex((candidate) => (
      candidate.gameSpaceId === key.gameSpaceId && candidate.boostEventId === key.boostEventId
    )) === index);
};

const buildSelectedEventKeysCte = (selectedEventKeys) => {
  const rows = normalizeSelectedEventKeys(selectedEventKeys);
  if (!rows.length) {
    return "SELECT NULL AS game_space_id, NULL AS boost_event_id WHERE 1 = 0";
  }

  return rows.map((key) => (
    `SELECT '${escapeSqlLiteral(key.gameSpaceId)}' AS game_space_id, '${escapeSqlLiteral(key.boostEventId)}' AS boost_event_id`
  )).join(" UNION ALL ");
};

const buildSelectedEventsCte = (selectedEventKeys, startUtc, endUtc) => `
    selected_event_keys AS (
      ${buildSelectedEventKeysCte(selectedEventKeys)}
    ),
    selected_events AS (
      SELECT
        sek.game_space_id,
        sek.boost_event_id,
        be.start_datetime,
        be.end_datetime,
        sn.official_name AS season_name,
        sn.abbreviation_name AS season_abbreviation,
        COALESCE(
          (
            SELECT NULLIF(TRIM(bei.event_name), '')
            FROM boost_event_info bei
            WHERE bei.boost_event_id = be.id
              AND bei.language_code = 'JA'
            LIMIT 1
          ),
          (
            SELECT NULLIF(TRIM(bel.label), '')
            FROM boost_event_label bel
            WHERE bel.boost_event_id = be.id
              AND bel.language_code = 'JA'
              AND bel.sort_order = 1
            LIMIT 1
          ),
          CAST(be.id AS CHAR)
        ) AS boost_event_name,
        GREATEST(be.start_datetime, '${startUtc}') AS effective_start_utc,
        LEAST(be.end_datetime, '${endUtc}') AS effective_end_utc
      FROM selected_event_keys sek
      JOIN boost_event be
        ON be.id = sek.boost_event_id
       AND be.game_space_id = sek.game_space_id
      JOIN season_name sn
        ON sn.season_id = be.game_space_id
       AND sn.language_code = 'JA'
      WHERE GREATEST(be.start_datetime, '${startUtc}') < LEAST(be.end_datetime, '${endUtc}')
    )`;

const buildEventDailyTrendQuery = (selectedEventKeys, startUtc, endUtc, startJst, endJst) => {
  return {
    query: `
      WITH RECURSIVE date_series AS (
        SELECT DATE('${startJst}') AS day_jst
        UNION ALL
        SELECT DATE_ADD(day_jst, INTERVAL 1 DAY)
        FROM date_series
        WHERE day_jst < DATE('${endJst}')
      ),
      ${buildSelectedEventsCte(selectedEventKeys, startUtc, endUtc)},
      approved_rows AS (
        SELECT DISTINCT
          DATE_FORMAT(CONVERT_TZ(rre.occurred_datetime, 'UTC', 'Asia/Tokyo'), '%Y-%m-%d') AS day_jst,
          rr.power_asset_id,
          rr.player_id
        FROM selected_events se
        JOIN review_request_event rre
          ON rre.game_space_id = se.game_space_id
         AND rre.type = 'APPROVED'
         AND rre.occurred_datetime >= se.effective_start_utc
         AND rre.occurred_datetime < se.effective_end_utc
        JOIN review_request_event_of_approved rreoa
          ON rreoa.id = rre.id
        JOIN review_request rr
          ON rr.id = rre.review_request_id
         AND rr.game_space_id = rre.game_space_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
      ),
      daily_rows AS (
        SELECT
          day_jst,
          COUNT(DISTINCT power_asset_id) AS unique_shot_count,
          COUNT(DISTINCT power_asset_id) AS review_ok_count,
          COUNT(DISTINCT player_id) AS unique_shooter_count
        FROM approved_rows
        GROUP BY day_jst
      )
      SELECT
        ds.day_jst,
        COALESCE(dr.unique_shot_count, 0) AS unique_shot_count,
        COALESCE(dr.review_ok_count, 0) AS review_ok_count,
        COALESCE(dr.unique_shooter_count, 0) AS unique_shooter_count
      FROM date_series ds
      LEFT JOIN daily_rows dr
        ON dr.day_jst = ds.day_jst
      ORDER BY ds.day_jst
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildSeasonRangeQuery = (seasonIds) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");

  return {
    query: `
      SELECT
        DATE_FORMAT(MIN(CONVERT_TZ(gt.start_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS start_jst,
        DATE_FORMAT(MAX(CONVERT_TZ(gt.end_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS end_jst
      FROM game_space gs
      JOIN game_term gt
        ON gt.id = gs.game_term_id
      WHERE gs.id IN (${seasonIdList});
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildRegularSeasonCatalogQuery = () => ({
  query: `
    SELECT
      gs.id AS season_id,
      sn.official_name AS season_name,
      sn.abbreviation_name AS season_abbreviation,
      gt.start_datetime AS season_start,
      gt.end_datetime AS season_end
    FROM game_space gs
    JOIN game_term gt
      ON gt.id = gs.game_term_id
    JOIN season_name sn
      ON sn.season_id = gs.id
     AND sn.language_code = 'JA'
    WHERE sn.official_name REGEXP '^レギュラーシーズン[0-9]+$'
    ORDER BY gt.start_datetime DESC, gs.id
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventMetadataQuery = (seasonIds) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");

  return {
    query: `
      SELECT
        be.game_space_id,
        be.id AS boost_event_id,
        sn.official_name AS season_name,
        sn.abbreviation_name AS season_abbreviation,
        COALESCE(
          (
            SELECT NULLIF(TRIM(bei.event_name), '')
            FROM boost_event_info bei
            WHERE bei.boost_event_id = be.id
              AND bei.language_code = 'JA'
            LIMIT 1
          ),
          (
            SELECT NULLIF(TRIM(bel.label), '')
            FROM boost_event_label bel
            WHERE bel.boost_event_id = be.id
              AND bel.language_code = 'JA'
              AND bel.sort_order = 1
            LIMIT 1
          ),
          CAST(be.id AS CHAR)
        ) AS boost_event_name,
        be.start_datetime,
        be.end_datetime
      FROM boost_event be
      JOIN season_name sn
        ON sn.season_id = be.game_space_id
       AND sn.language_code = 'JA'
      WHERE be.game_space_id IN (${seasonIdList})
      ORDER BY be.start_datetime DESC, be.id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildEventRowsQuery = (selectedEventKeys, startUtc, endUtc) => {
  return {
    query: `
      WITH ${buildSelectedEventsCte(selectedEventKeys, startUtc, endUtc)},
      selected_events_with_budget AS (
        SELECT se.*, cb.id AS capital_budget_id
        FROM selected_events se
        JOIN capital_budget cb
          ON cb.boost_event_id = se.boost_event_id
      ),
      target_assets AS (
        SELECT
          se.game_space_id,
          se.boost_event_id,
          btal.power_asset_id,
          b.id AS budget_id,
          se.start_datetime
        FROM selected_events_with_budget se
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
         AND b.power_asset_id = btal.power_asset_id
        LEFT JOIN budget_event pre_budget_event
          ON pre_budget_event.budget_id = b.id
         AND pre_budget_event.occurred_datetime < se.start_datetime
        LEFT JOIN budget_event_of_reward_granted pre_reward
          ON pre_reward.id = pre_budget_event.id
        WHERE pre_reward.id IS NULL
          AND NOT EXISTS (
            SELECT 1
            FROM power_asset_event ppae
            WHERE ppae.game_space_id = se.game_space_id
              AND ppae.power_asset_id = btal.power_asset_id
              AND ppae.type = 'PHOTOGRAPH'
              AND ppae.occurred_datetime < se.start_datetime
          )
      ),
      approved_rows AS (
        SELECT DISTINCT
          se.game_space_id,
          se.boost_event_id,
          rr.power_asset_id,
          rr.player_id,
          rre.id AS review_event_id
        FROM selected_events se
        JOIN review_request_event rre
          ON rre.game_space_id = se.game_space_id
         AND rre.type = 'APPROVED'
         AND rre.occurred_datetime >= se.effective_start_utc
         AND rre.occurred_datetime < se.effective_end_utc
        JOIN review_request_event_of_approved rreoa
          ON rreoa.id = rre.id
        JOIN review_request rr
          ON rr.id = rre.review_request_id
         AND rr.game_space_id = rre.game_space_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
      ),
      event_target_metrics AS (
        SELECT
          game_space_id,
          boost_event_id,
          COUNT(DISTINCT power_asset_id) AS target_count
        FROM target_assets
        GROUP BY game_space_id, boost_event_id
      ),
      event_approved_metrics AS (
        SELECT
          game_space_id,
          boost_event_id,
          COUNT(DISTINCT power_asset_id) AS shot_count,
          COUNT(DISTINCT review_event_id) AS review_event_count
        FROM approved_rows
        GROUP BY game_space_id, boost_event_id
      ),
      event_metrics AS (
        SELECT
          se.game_space_id,
          se.boost_event_id,
          se.season_name,
          se.season_abbreviation,
          se.boost_event_name,
          se.start_datetime,
          se.end_datetime,
          COALESCE(etm.target_count, 0) AS target_count,
          COALESCE(eam.shot_count, 0) AS shot_count,
          COALESCE(eam.review_event_count, 0) AS review_event_count
        FROM selected_events se
        LEFT JOIN event_target_metrics etm
          ON etm.game_space_id = se.game_space_id
         AND etm.boost_event_id = se.boost_event_id
        LEFT JOIN event_approved_metrics eam
          ON eam.game_space_id = se.game_space_id
         AND eam.boost_event_id = se.boost_event_id
      ),
      integrated_metrics AS (
        SELECT
          (SELECT COUNT(DISTINCT power_asset_id) FROM target_assets) AS target_count,
          (SELECT COUNT(DISTINCT power_asset_id) FROM approved_rows) AS shot_count,
          (SELECT COUNT(DISTINCT player_id) FROM approved_rows) AS approved_unique_player_count,
          (SELECT COUNT(DISTINCT review_event_id) FROM approved_rows) AS review_event_count
      )
      SELECT
        CONVERT('event' USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS row_type,
        CONVERT(em.game_space_id USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS game_space_id,
        CONVERT(em.boost_event_id USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS boost_event_id,
        CONVERT(em.season_name USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS season_name,
        CONVERT(em.season_abbreviation USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS season_abbreviation,
        CONVERT(em.boost_event_name USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS boost_event_name,
        em.start_datetime,
        em.end_datetime,
        em.target_count,
        em.shot_count,
        em.review_event_count,
        NULL AS approved_unique_player_count
      FROM event_metrics em
      UNION ALL
      SELECT
        CONVERT('summary' USING utf8mb4) COLLATE ${KPI_SQL_TEXT_COLLATION} AS row_type,
        ${collatedSqlNull()} AS game_space_id,
        ${collatedSqlNull()} AS boost_event_id,
        ${collatedSqlNull()} AS season_name,
        ${collatedSqlNull()} AS season_abbreviation,
        ${collatedSqlNull()} AS boost_event_name,
        NULL AS start_datetime,
        NULL AS end_datetime,
        im.target_count,
        im.shot_count,
        im.review_event_count,
        im.approved_unique_player_count
      FROM integrated_metrics im
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildEventApprovalTargetAssetsBase = (selectedEventKeys, startUtc, endUtc) => `
    WITH ${buildSelectedEventsCte(selectedEventKeys, startUtc, endUtc)},
    target_assets AS (
      SELECT DISTINCT
        se.game_space_id,
        se.boost_event_id,
        btal.power_asset_id
      FROM selected_events se
      JOIN boost_target_asset_list btal
        ON btal.boost_event_id = se.boost_event_id
    ),
    approved_rows AS (
      SELECT DISTINCT
        rr.power_asset_id,
        rr.player_id,
        rre.id AS review_event_id,
        rre.occurred_datetime
      FROM selected_events se
      JOIN review_request_event rre
        ON rre.game_space_id = se.game_space_id
       AND rre.type = 'APPROVED'
       AND rre.occurred_datetime >= se.effective_start_utc
       AND rre.occurred_datetime < se.effective_end_utc
      JOIN review_request_event_of_approved rreoa
        ON rreoa.id = rre.id
      JOIN review_request rr
        ON rr.id = rre.review_request_id
       AND rr.game_space_id = rre.game_space_id
      JOIN target_assets ta
        ON ta.game_space_id = se.game_space_id
       AND ta.boost_event_id = se.boost_event_id
       AND ta.power_asset_id = rr.power_asset_id
    )
`;

const buildEventReviewOkCountQuery = (selectedEventKeys, startUtc, endUtc) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(selectedEventKeys, startUtc, endUtc)}
    SELECT COUNT(DISTINCT power_asset_id) AS approved_review_request_count
    FROM approved_rows
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventUniqueShooterCountQuery = (selectedEventKeys, startUtc, endUtc) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(selectedEventKeys, startUtc, endUtc)}
    SELECT COUNT(DISTINCT player_id) AS approved_unique_player_count
    FROM approved_rows
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventFirstShooterCountQuery = (selectedEventKeys, startUtc, endUtc) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(selectedEventKeys, startUtc, endUtc)}
    SELECT COUNT(*) AS first_approved_count
    FROM (
      SELECT player_id, MIN(occurred_datetime) AS first_approved_datetime
      FROM approved_rows
      GROUP BY player_id
    ) AS approved_players
    WHERE approved_players.first_approved_datetime >= '${startUtc}'
      AND approved_players.first_approved_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

/*
 * The previous event-level builders are intentionally retained below only as
 * historical context in the diff. Overview requests use the selected-event
 * builders above so event-level DISTINCT values are never summed.
 */
/* Legacy definitions below are removed from the active path. */
/*
      target_assets AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT btal.power_asset_id) AS target_count
        FROM selected_events se
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
        JOIN budget b
          ON b.capital_budget_id = se.capital_budget_id
         AND b.power_asset_id = btal.power_asset_id
        LEFT JOIN budget_event pre_budget_event
          ON pre_budget_event.budget_id = b.id
         AND pre_budget_event.occurred_datetime < se.start_datetime
        LEFT JOIN budget_event_of_reward_granted pre_reward
          ON pre_reward.id = pre_budget_event.id
        WHERE NOT EXISTS (
          SELECT 1
          FROM power_asset_event ppae
          WHERE ppae.game_space_id = se.game_space_id
            AND ppae.power_asset_id = btal.power_asset_id
            AND ppae.type = 'PHOTOGRAPH'
            AND ppae.occurred_datetime < se.start_datetime
        )
        AND pre_reward.id IS NULL
        GROUP BY se.boost_event_id
      ),
      approved_shot_rows AS (
        SELECT
          se.boost_event_id,
          COUNT(DISTINCT rr.power_asset_id) AS shot_count
        FROM selected_events se
        JOIN review_request_event rre
          ON rre.game_space_id = se.game_space_id
         AND rre.type = 'APPROVED'
        JOIN review_request_event_of_approved rreoa
          ON rreoa.id = rre.id
        JOIN review_request rr
          ON rr.id = rre.review_request_id
        JOIN boost_target_asset_list btal
          ON btal.boost_event_id = se.boost_event_id
         AND btal.power_asset_id = rr.power_asset_id
        WHERE rre.occurred_datetime >= '${startUtc}'
          AND rre.occurred_datetime < '${endUtc}'
        GROUP BY se.boost_event_id
  )
  SELECT
        se.game_space_id,
        se.boost_event_id,
        se.boost_event_name,
        se.start_datetime,
        se.end_datetime,
        COALESCE(ta.target_count, 0) AS target_count,
        COALESCE(asr.shot_count, 0) AS shot_count
      FROM selected_events se
      LEFT JOIN target_assets ta
        ON ta.boost_event_id = se.boost_event_id
      LEFT JOIN approved_shot_rows asr
        ON asr.boost_event_id = se.boost_event_id
      ORDER BY se.start_datetime, se.boost_event_id
    `,
    database: "KpiDashboard",
    type: "list",
  };
};
*/

/* const buildEventApprovalTargetAssetsBaseLegacy = (seasonIds, startUtc, endUtc, selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const seasonIdList = seasonIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const boostEventFilterClause = buildBoostEventFilterClause(selectedBoostEventId);

  return `
    WITH selected_events AS (
      SELECT
        be.id AS boost_event_id,
        be.game_space_id,
        be.start_datetime,
        be.end_datetime
      FROM boost_event be
      WHERE be.game_space_id IN (${seasonIdList})
        AND be.start_datetime < '${endUtc}'
        AND be.end_datetime >= '${startUtc}'
        ${boostEventFilterClause}
    ),
    target_assets AS (
      SELECT DISTINCT
        btal.power_asset_id
      FROM selected_events se
      JOIN boost_target_asset_list btal
        ON btal.boost_event_id = se.boost_event_id
    )
  `;
};

const buildEventReviewOkCountQueryLegacy = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(DISTINCT rr.power_asset_id) AS approved_review_request_count
    FROM review_request_event_of_approved rreoa
    JOIN review_request_event rre
      ON rre.id = rreoa.id
    JOIN review_request rr
      ON rr.id = rre.review_request_id
    JOIN target_assets ta
      ON ta.power_asset_id = rr.power_asset_id
    WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventUniqueShooterCountQueryLegacy = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(DISTINCT rr.player_id) AS approved_unique_player_count
    FROM review_request_event_of_approved rreoa
    JOIN review_request_event rre
      ON rre.id = rreoa.id
    JOIN review_request rr
      ON rr.id = rre.review_request_id
    JOIN target_assets ta
      ON ta.power_asset_id = rr.power_asset_id
    WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
});

const buildEventFirstShooterCountQueryLegacy = (seasonIds, startUtc, endUtc, selectedBoostEventId) => ({
  query: `
    ${buildEventApprovalTargetAssetsBase(seasonIds, startUtc, endUtc, selectedBoostEventId)}
    SELECT
      COUNT(*) AS first_approved_count
    FROM (
      SELECT
        rr.player_id,
        MIN(rre.occurred_datetime) AS first_approved_datetime
      FROM review_request_event_of_approved rreoa
      JOIN review_request_event rre
        ON rre.id = rreoa.id
      JOIN review_request rr
        ON rr.id = rre.review_request_id
      JOIN target_assets ta
        ON ta.power_asset_id = rr.power_asset_id
      WHERE rr.game_space_id IN (${seasonIds.map((seasonId) => `'${seasonId}'`).join(", ")})
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime < '${endUtc}'
      GROUP BY rr.player_id
    ) AS approved_players
    WHERE approved_players.first_approved_datetime >= '${startUtc}'
      AND approved_players.first_approved_datetime < '${endUtc}'
  `,
  database: "KpiDashboard",
  type: "list",
}); */

const parseCountRows = (rows, key) => Number(parseRows(rows)?.[0]?.[key] || 0);

const parseDateRange = (dateRange, fallbackStartJst, fallbackEndJst) => {
  if (Array.isArray(dateRange) && dateRange.length >= 2) {
    const [startDate, endDate] = dateRange;
    if (startDate && endDate) {
      return [startDate, endDate];
    }
  }

  return [fallbackStartJst, fallbackEndJst];
};

const capEndDateToTodayJst = (endJst) => {
  const todayJst = moment.tz("Asia/Tokyo").format("YYYY-MM-DD");
  if (!endJst) {
    return todayJst;
  }

  return moment(endJst).isAfter(moment(todayJst)) ? todayJst : endJst;
};

const parseRows = (rows) => {
  if (Array.isArray(rows)) return rows;
  if (rows == null) return [];

  if (typeof rows === "string") {
    try {
      return parseRows(JSON.parse(rows));
    } catch (error) {
      return [];
    }
  }

  if (typeof rows !== "object") return [];

  const candidates = [rows.body, rows.data, rows.rows, rows.result, rows.records];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const parsed = parseRows(candidate);
    if (parsed.length > 0) return parsed;
  }

  return [];
};

const normalizeEventRows = (rows) => (
  Array.isArray(rows) ? rows : []
).map((row) => ({
  rowType: row?.row_type || "event",
  seasonId: row?.game_space_id || null,
  seasonName: row?.season_name || row?.game_space_id || "",
  seasonAbbreviation: row?.season_abbreviation || "",
  id: row?.boost_event_id || null,
  name: row?.boost_event_name || row?.boost_event_id || "ブーストイベント",
  startDatetime: row?.start_datetime || null,
  endDatetime: row?.end_datetime || null,
  period: row?.start_datetime && row?.end_datetime
    ? `${moment.tz(row.start_datetime, "UTC").tz("Asia/Tokyo").format("YYYY/MM/DD")} - ${moment.tz(row.end_datetime, "UTC").tz("Asia/Tokyo").format("YYYY/MM/DD")}`
    : "",
  targetCount: Number(row?.target_count || 0),
  shotCount: Number(row?.shot_count || 0),
  payoutAmount: Number(row?.payout_amount || 0),
  reviewEventCount: Number(row?.review_event_count || 0),
  approvedUniquePlayerCount: Number(row?.approved_unique_player_count || 0),
}));

const normalizeSeasonRows = (rows) => (
  Array.isArray(rows) ? rows : []
).filter((row) => row?.season_id).map((row) => ({
  value: row.season_id,
  label: row.season_name || row.season_id,
  abbreviation: row.season_abbreviation || "",
  seasonStart: row.season_start || null,
  seasonEnd: row.season_end || null,
}));

const getLatestStartedEvent = (eventRows, now = moment.utc()) => (
  (eventRows || [])
    .filter((row) => row?.id && row?.startDatetime && moment.utc(row.startDatetime).isSameOrBefore(now))
    .sort((left, right) => moment.utc(right.startDatetime).valueOf() - moment.utc(left.startDatetime).valueOf())[0] || null
);

const aggregateTrendDays = (rows) => {
  const dayMap = new Map();

  (rows || []).forEach((row) => {
    const day = row.startDatetime ? moment.tz(row.startDatetime, "UTC").tz("Asia/Tokyo").format("YYYY-MM-DD") : "";
    if (!day) {
      return;
    }

    const current = dayMap.get(day) || {
      targetCount: 0,
      shotCount: 0,
      eventCount: 0,
    };

    current.targetCount += Number(row.targetCount) || 0;
    current.shotCount += Number(row.shotCount) || 0;
    current.eventCount += 1;
    dayMap.set(day, current);
  });

  const days = Array.from(dayMap.keys()).sort();

  return {
    date: days.map((day) => moment(day).format("M/D")),
    uniqueShots: days.map((day) => Number(dayMap.get(day)?.targetCount) || 0),
    reviewerOk: days.map((day) => Number(dayMap.get(day)?.shotCount) || 0),
    uniqueShooters: days.map((day) => Number(dayMap.get(day)?.eventCount) || 0),
  };
};

const aggregateSeasonChart = (rows, seasonOptions, selectedSeasonIds) => {
  const seasonIdOrder = sortSeasonIds(selectedSeasonIds);
  const seasonLabels = seasonIdOrder.map((seasonId) => seasonOptions.find((option) => option.value === seasonId)?.label || resolveSeasonLabel(seasonId));
  const seasonTotals = new Map();

  seasonIdOrder.forEach((seasonId) => {
    seasonTotals.set(seasonId, {
      targetCount: 0,
      shotCount: 0,
    });
  });

  (rows || []).forEach((row) => {
    const seasonId = row.seasonId;
    if (!seasonTotals.has(seasonId)) {
      return;
    }

    const current = seasonTotals.get(seasonId);
    current.targetCount += Number(row.targetCount) || 0;
    current.shotCount += Number(row.shotCount) || 0;
    seasonTotals.set(seasonId, current);
  });

  return {
    labels: seasonLabels,
    series: [
      {
        name: "対象電柱数",
        data: seasonIdOrder.map((seasonId) => Number(seasonTotals.get(seasonId)?.targetCount) || 0),
      },
      {
        name: "撮影数",
        data: seasonIdOrder.map((seasonId) => Number(seasonTotals.get(seasonId)?.shotCount) || 0),
      },
    ],
  };
};

const buildEventRowsFromQuery = (rows, selectedBoostEventId = ALL_BOOST_EVENTS_VALUE) => {
  const normalizedRows = normalizeEventRows(rows);
  const summaryRow = normalizedRows.find((row) => row.rowType === "summary");
  const eventRows = normalizedRows.filter((row) => row.rowType !== "summary");
  const summarySourceRows = isAllBoostEvents(selectedBoostEventId)
    ? eventRows
    : eventRows.filter((row) => row.id === selectedBoostEventId);

  const calculatedSummaryCounts = (Array.isArray(summarySourceRows) ? summarySourceRows : []).reduce((acc, row) => {
    acc.targetCount += Number(row.targetCount) || 0;
    acc.uniqueShotCount += Number(row.shotCount) || 0;
    return acc;
  }, {
    targetCount: 0,
    uniqueShotCount: 0,
  });
  const summaryCounts = summaryRow && isAllBoostEvents(selectedBoostEventId)
    ? {
      targetCount: Number(summaryRow.targetCount || 0),
      uniqueShotCount: Number(summaryRow.shotCount || 0),
      approvedUniquePlayerCount: Number(summaryRow.approvedUniquePlayerCount || 0),
      reviewEventCount: Number(summaryRow.reviewEventCount || 0),
    }
    : calculatedSummaryCounts;

  return {
    eventRows,
    summaryCounts,
  };
};

const buildEventTrend = (rows) => {
  const dailyRows = Array.isArray(rows) ? rows : [];
  return {
    date: dailyRows.map((row) => {
      const day = row?.day_jst || "";
      return day ? moment.utc(day).tz("Asia/Tokyo").format("M/D") : "";
    }),
    uniqueShots: dailyRows.map((row) => Number(row?.unique_shot_count || 0)),
    reviewerOk: dailyRows.map((row) => Number(row?.review_ok_count || 0)),
    uniqueShooters: dailyRows.map((row) => Number(row?.unique_shooter_count || 0)),
  };
};

const buildEventKpis = (summary) => {
  const uniqueShotCount = Number(summary.uniqueShotCount || 0);
  const targetCount = Number(summary.targetCount || 0);
  const reviewOkCount = Number(summary.reviewOkCount || 0);
  const uniqueShooterCount = Number(summary.uniqueShooterCount || 0);
  const firstShooterCount = Number(summary.firstShooterCount || 0);
  const uniqueShotLoading = Boolean(summary.uniqueShotLoading);
  const achievementRateLoading = Boolean(summary.achievementRateLoading);
  const reviewOkLoading = Boolean(summary.reviewOkLoading);
  const uniqueShooterLoading = Boolean(summary.uniqueShooterLoading);
  const firstShooterLoading = Boolean(summary.firstShooterLoading);
  const achievementRate = targetCount > 0 ? (reviewOkCount / targetCount) * 100 : 0;
  const okRate = uniqueShotCount > 0 ? (reviewOkCount / uniqueShotCount) * 100 : 0;

  return [
    {
      key: "uniqueShot",
      title: "ユニーク撮影本数",
      value: uniqueShotLoading ? "--" : formatNumber(uniqueShotCount),
      subtitle: uniqueShotLoading ? "" : "ブースト対象",
      loading: uniqueShotLoading,
    },
    {
      key: "achievementRate",
      title: "撮影達成率",
      value: achievementRateLoading ? "--" : `${achievementRate.toFixed(1)}%`,
      subtitle: achievementRateLoading ? "対象 --本" : `対象 ${formatNumber(targetCount)}本`,
      loading: achievementRateLoading,
    },
    {
      key: "reviewOk",
      title: "レビューOK数",
      value: reviewOkLoading ? "--" : formatNumber(reviewOkCount),
      subtitle: reviewOkLoading ? "OK率 --%" : `OK率 ${okRate.toFixed(1)}%`,
      loading: reviewOkLoading,
    },
    {
      key: "uniqueShooter",
      title: "ユニーク撮影者数",
      value: uniqueShooterLoading ? "--" : formatNumber(uniqueShooterCount),
      subtitle: "累計参加者",
      loading: uniqueShooterLoading,
    },
    {
      key: "firstShooter",
      title: "初回撮影者数",
      value: firstShooterLoading ? "--" : formatNumber(firstShooterCount),
      subtitle: "今期新規",
      loading: firstShooterLoading,
    },
  ];
};

const createEmptyEventSummaryState = () => ({
  uniqueShotCount: 0,
  targetCount: 0,
  reviewOkCount: 0,
  uniqueShooterCount: 0,
  firstShooterCount: 0,
  uniqueShotLoading: true,
  achievementRateLoading: true,
  reviewOkLoading: true,
  uniqueShooterLoading: true,
  firstShooterLoading: true,
  errors: {},
});

function* handleRegularSeasonEventBootstrapData() {
  try {
    yield put(setPicTreeRegularSeasonEventPageData({
      bootstrapEventLoading: true,
      bootstrapEventReady: false,
      eventRowsLoading: true,
      eventRows: [],
      eventRowsError: null,
      selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
      selectedEventKeys: [],
      draftSelectedEventKeys: [],
      selectedDateRange: [null, null],
      draftDateRange: [null, null],
    }));

    const jwtToken = yield call(getJwtToken);

    const seasonCatalogResult = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [buildRegularSeasonCatalogQuery(), ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const seasonOptions = normalizeSeasonRows(parseRows(seasonCatalogResult));
    const seasonIdList = sortSeasonIds(seasonOptions.map((option) => option.value));
    if (!seasonIdList.length) {
      yield put(setPicTreeRegularSeasonEventPageData({
        bootstrapEventLoading: false,
        bootstrapEventReady: true,
        eventRowsLoading: false,
        eventRows: [],
        eventRowsError: null,
        seasonOptions,
        selectedSeasonId: null,
        selectedSeasonIds: [],
        selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
        selectedEventKeys: [],
        draftSelectedEventKeys: [],
        selectedDateRange: [null, null],
        draftDateRange: [null, null],
      }));
      return;
    }

    const eventRowsQuery = buildEventMetadataQuery(seasonIdList);

    const eventRowsResult = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [eventRowsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const { eventRows } = buildEventRowsFromQuery(parseRows(eventRowsResult), ALL_BOOST_EVENTS_VALUE);
    const firstEventRow = getLatestStartedEvent(eventRows);
    const firstEventId = firstEventRow?.id || ALL_BOOST_EVENTS_VALUE;
    const firstEventKeys = firstEventRow
      ? [{ gameSpaceId: firstEventRow.seasonId, boostEventId: firstEventRow.id }]
      : [];
    const firstStartJst = firstEventRow?.startDatetime
      ? moment.utc(firstEventRow.startDatetime).tz("Asia/Tokyo").format("YYYY-MM-DD")
      : null;
    const firstEndJst = firstEventRow?.endDatetime
      ? moment.min(
        moment.utc(firstEventRow.endDatetime).tz("Asia/Tokyo").startOf("day"),
        moment().tz("Asia/Tokyo").startOf("day")
      ).format("YYYY-MM-DD")
      : null;

    yield put(setPicTreeRegularSeasonEventPageData({
      selectedSeasonId: firstEventRow?.seasonId || seasonIdList[0] || null,
      selectedSeasonIds: seasonIdList,
      selectedBoostEventId: firstEventId,
      selectedEventKeys: firstEventKeys,
      draftSelectedEventKeys: firstEventKeys,
      selectedDateRange: [firstStartJst, firstEndJst],
      draftDateRange: [firstStartJst, firstEndJst],
      seasonOptions,
      bootstrapEventLoading: false,
      bootstrapEventReady: true,
      eventRows,
      eventRowsLoading: false,
      eventRowsError: null,
      error: null,
    }));
  } catch (error) {
    yield put(setPicTreeRegularSeasonEventPageData({
      bootstrapEventLoading: false,
      bootstrapEventReady: false,
      eventRowsLoading: false,
      eventRows: [],
      eventRowsError: { message: error?.message || "イベント一覧の取得に失敗しました" },
      selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
    }));
  }
}

function* handleRegularSeasonEventPageData(action) {
  try {
    yield put(setPicTreeRegularSeasonEventPageLoading(true));
    yield put(setPageTopLoader(true));

    const selectedSeasonId = action?.payload?.selectedSeasonId || null;
    const selectedSeasonIds = normalizeSelectionSeasonIds(action?.payload?.selectedSeasonIds, selectedSeasonId);
    const selectedBoostEventId = action?.payload?.selectedBoostEventId || ALL_BOOST_EVENTS_VALUE;
    const initialEventRows = Array.isArray(action?.payload?.eventRows) ? action.payload.eventRows : [];
    const selectedEventKeys = normalizeSelectedEventKeys(
      action?.payload?.selectedEventKeys,
      initialEventRows,
      selectedBoostEventId
    );
    const selectedDateRange = action?.payload?.selectedDateRange;

    if (!selectedEventKeys.length) {
      yield put(setPicTreeRegularSeasonEventPageData({
        ...emptyPageData(selectedSeasonId, selectedSeasonIds, []),
        selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
        selectedEventKeys: [],
        draftSelectedEventKeys: [],
        selectedDateRange: Array.isArray(selectedDateRange) ? selectedDateRange : [null, null],
        kpis: readyEmptyKpis(),
        trend: emptyTrend(),
        trendLoading: false,
        trendRows: [],
        trendRowsLoading: false,
        eventRows: initialEventRows,
        eventRowsLoading: false,
      }));
      yield put(setPageTopLoader(false));
      yield put(setPicTreeRegularSeasonEventPageLoading(false));
      return;
    }

    yield put(setPicTreeRegularSeasonEventPageData({
      ...emptyPageData(selectedSeasonId, selectedSeasonIds, selectedEventKeys),
      selectedBoostEventId,
      eventRows: initialEventRows,
      error: null,
      selectedDateRange: Array.isArray(selectedDateRange) && selectedDateRange.length >= 2
        ? selectedDateRange
        : [null, null],
    }));

    const jwtToken = yield call(getJwtToken);
    const seasonIdList = sortSeasonIds(selectedSeasonIds);

    if (!seasonIdList.length) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: readyEmptyKpis(),
        trend: emptyTrend(),
        trendLoading: false,
        trendRows: [],
        trendRowsLoading: false,
        seasonChart: emptySeasonChart(),
        seasonChartLoading: false,
        eventRows: [],
        eventRowsLoading: false,
      }));
      return;
    }

    const seasonRangeRows = yield call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [buildSeasonRangeQuery(seasonIdList), ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );

    const seasonRange = parseRows(seasonRangeRows)?.[0] || {};
    const [startJstRaw, endJstRaw] = parseDateRange(selectedDateRange, seasonRange?.start_jst, seasonRange?.end_jst);
    const startJst = startJstRaw;
    const endJst = capEndDateToTodayJst(endJstRaw);

    if (!startJst || !endJst) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: readyEmptyKpis(),
        trend: emptyTrend(),
        trendRows: [],
        eventRowsLoading: false,
        trendLoading: false,
        seasonChart: emptySeasonChart(),
        seasonChartLoading: false,
        eventRows: [],
        selectedBoostEventId,
        selectedDateRange: [startJst || null, endJst || null],
      }));
      return;
    }

    const effectiveSelectedBoostEventId = selectedBoostEventId;
    const effectiveStartJst = startJst;
    const effectiveEndJst = endJst;
    const startUtc = moment.tz(effectiveStartJst, "Asia/Tokyo").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment.tz(effectiveEndJst, "Asia/Tokyo").add(1, "day").startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const eventRowsQuery = buildEventRowsQuery(
      selectedEventKeys,
      startUtc,
      endUtc
    );
    const eventDailyTrendQuery = buildEventDailyTrendQuery(selectedEventKeys, startUtc, endUtc, effectiveStartJst, effectiveEndJst);
    const eventReviewOkCountQuery = buildEventReviewOkCountQuery(selectedEventKeys, startUtc, endUtc);
    const eventUniqueShooterCountQuery = buildEventUniqueShooterCountQuery(selectedEventKeys, startUtc, endUtc);
    const eventFirstShooterCountQuery = buildEventFirstShooterCountQuery(selectedEventKeys, startUtc, endUtc);

    const summaryState = createEmptyEventSummaryState();
    let latestTrendRows = [];
    let trendLoading = true;
    const syncAchievementLoading = () => {
      summaryState.achievementRateLoading = summaryState.uniqueShotLoading || summaryState.reviewOkLoading;
    };
    const publishEventPageData = function* (overrides = {}) {
      yield put(setPicTreeRegularSeasonEventPageData({
        kpis: buildEventKpis(summaryState),
        trend: buildEventTrend(latestTrendRows),
        trendLoading,
        trendRows: latestTrendRows,
        trendRowsLoading: trendLoading,
        selectedBoostEventId: effectiveSelectedBoostEventId,
        selectedDateRange: [effectiveStartJst, effectiveEndJst],
        ...overrides,
      }));
    };

    const fetchEventRowsSection = function* () {
      try {
        const eventRowsResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventRowsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        const { eventRows: metricRows, summaryCounts } = buildEventRowsFromQuery(
          parseRows(eventRowsResult),
          effectiveSelectedBoostEventId
        );
        const metricRowsByKey = new Map(metricRows.map((row) => [`${row.seasonId}:${row.id}`, row]));
        const eventRows = initialEventRows.length > 0
          ? initialEventRows.map((row) => ({
            ...row,
            ...(metricRowsByKey.get(`${row.seasonId}:${row.id}`) || {}),
          }))
          : metricRows;

        summaryState.uniqueShotCount = Number(summaryCounts.uniqueShotCount || 0);
        summaryState.targetCount = Number(summaryCounts.targetCount || 0);
        summaryState.uniqueShotLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
          eventRows,
          eventRowsLoading: false,
        });
      } catch (error) {
        yield publishEventPageData({
          // KPI failure must not erase the independently loaded event catalog.
          eventRows: initialEventRows,
          eventRowsLoading: false,
          error: { section: "eventRows", message: error?.message || "Overview KPI取得に失敗しました" },
        });
      }
    };

    const fetchEventTrendSection = function* () {
      try {
        const eventDailyTrendResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventDailyTrendQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        latestTrendRows = parseRows(eventDailyTrendResult);
        trendLoading = false;

        yield publishEventPageData({
          trend: buildEventTrend(latestTrendRows),
          trendLoading: false,
          trendRows: latestTrendRows,
          trendRowsLoading: false,
        });
      } catch (error) {
        latestTrendRows = [];
        trendLoading = false;

        yield publishEventPageData({
          trend: emptyTrend(),
          trendLoading: false,
          trendRows: [],
          trendRowsLoading: false,
          error: { section: "trend", message: error?.message || "日別データ取得に失敗しました" },
        });
      }
    };

    const fetchEventReviewOkSection = function* () {
      try {
        const eventReviewOkCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventReviewOkCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.reviewOkCount = parseCountRows(eventReviewOkCountResult, "approved_review_request_count");
        summaryState.reviewOkLoading = false;
        syncAchievementLoading();

        yield publishEventPageData({
        });
      } catch (error) {
        yield publishEventPageData({
          error: { section: "reviewOk", message: error?.message || "レビューOK数取得に失敗しました" },
        });
      }
    };

    const fetchEventUniqueShooterSection = function* () {
      try {
        const eventUniqueShooterCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventUniqueShooterCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.uniqueShooterCount = parseCountRows(eventUniqueShooterCountResult, "approved_unique_player_count");
        summaryState.uniqueShooterLoading = false;
        yield publishEventPageData({
        });
      } catch (error) {
        yield publishEventPageData({
          error: { section: "uniqueShooter", message: error?.message || "撮影者数取得に失敗しました" },
        });
      }
    };

    const fetchEventFirstShooterSection = function* () {
      try {
        const eventFirstShooterCountResult = yield call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [eventFirstShooterCountQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        );

        summaryState.firstShooterCount = parseCountRows(eventFirstShooterCountResult, "first_approved_count");
        summaryState.firstShooterLoading = false;
        yield publishEventPageData({
        });
      } catch (error) {
        yield publishEventPageData({
          error: { section: "firstShooter", message: error?.message || "初回撮影者数取得に失敗しました" },
        });
      }
    };

    yield call(fetchEventRowsSection);
    yield call(fetchEventTrendSection);
    yield call(fetchEventReviewOkSection);
    yield call(fetchEventUniqueShooterSection);
    yield call(fetchEventFirstShooterSection);
  } catch (error) {
    console.log(error);
    yield put(setPicTreeRegularSeasonEventPageData({
      kpis: readyEmptyKpis(),
      trend: emptyTrend(),
      trendLoading: false,
      trendRows: [],
      trendRowsLoading: false,
      eventRows: [],
      eventRowsLoading: false,
      error: { section: "page", message: error?.message || "Overviewデータ取得に失敗しました" },
    }));
  } finally {
    const wasCancelled = yield cancelled();
    if (!wasCancelled) {
      yield put(setPageTopLoader(false));
      yield put(setPicTreeRegularSeasonEventPageLoading(false));
    }
  }
}

export {
  buildEventRowsQuery,
  buildEventDailyTrendQuery,
  buildEventReviewOkCountQuery,
  buildEventUniqueShooterCountQuery,
  buildEventFirstShooterCountQuery,
  normalizeSelectedEventKeys,
  parseRows,
};

export default function* pictreeRegularSeasonEventSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_EVENT_BOOTSTRAP_DATA, handleRegularSeasonEventBootstrapData);
  yield takeLatest(PICTREE.GET_REGULAR_SEASON_EVENT_PAGE_DATA, handleRegularSeasonEventPageData);
}
